import React, { useState } from 'react';
import DataTableStructure from './DataTableStructure';
import { format, parseISO } from 'date-fns';
import { CURRENCY_SYMBOLS_TEXT } from '../utils/currencySymbols';
import { FaTrash } from 'react-icons/fa';
import DeleteModal from './DeleteModal';
import ExpenseModal from './ExpenseModal';

const AllTimeExpenseView = ({
  expenses,
  categories,
  onDelete,
  onAddExpense,
  updateCategories,
  currencies,
  onEdit,
}) => {
  const [tableFilter, setTableFilter] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Calculate lifetime total expenses
  const calculateLifetimeTotal = () => {
    const totals = {};
    Object.values(expenses).forEach(dayExpenses => {
      dayExpenses.forEach(expense => {
        try {
          const currency = expense.currency;
          const amount = parseFloat(expense.amount);
          
          if (!isNaN(amount)) {
            if (!totals[currency]) {
              totals[currency] = 0;
            }
            totals[currency] += amount;
          }
        } catch (error) {
          console.error('Error calculating expense:', error);
        }
      });
    });
    
    return Object.entries(totals)
      .map(([currency, amount]) => `${CURRENCY_SYMBOLS_TEXT[currency]}${amount.toFixed(2)}`)
      .join(', ');
  };

  // Find the oldest expense date
  const getFirstExpenseDate = () => {
    try {
      if (Object.keys(expenses).length === 0) {
        return null;
      }
      
      // Get all date strings and sort them chronologically
      const allDates = Object.keys(expenses)
        .filter(dateStr => {
          // Validate the date to avoid errors
          const parsedDate = parseISO(dateStr);
          return !isNaN(parsedDate.getTime());
        })
        .sort((a, b) => new Date(a) - new Date(b));
      
      if (allDates.length === 0) {
        return null;
      }
      
      // Format the oldest date to show only month and year
      const oldestDate = parseISO(allDates[0]);
      return format(oldestDate, 'MMM yyyy');
    } catch (error) {
      console.error('Error finding first expense date:', error);
      return null;
    }
  };

  // Transform expenses data for the table
  const tableData = React.useMemo(() => {
    return Object.entries(expenses)
      .flatMap(([date, dayExpenses]) => 
        dayExpenses.map(expense => {
          try {
            // Validate the date before formatting it
            const parsedDate = parseISO(date);
            if (isNaN(parsedDate.getTime())) {
              // If date is invalid, use current date as fallback
              const fallbackDate = new Date();
              return {
                ...expense,
                date: format(fallbackDate, 'yyyy-MM-dd'),
                formattedDate: format(fallbackDate, 'dd MMM yyyy'),
                formattedAmount: `${CURRENCY_SYMBOLS_TEXT[expense.currency]}${parseFloat(expense.amount).toFixed(2)}`
              };
            }
            
            return {
              ...expense,
              date: date,
              formattedDate: format(parsedDate, 'dd MMM yyyy'),
              formattedAmount: `${CURRENCY_SYMBOLS_TEXT[expense.currency]}${parseFloat(expense.amount).toFixed(2)}`
            };
          } catch (error) {
            console.error(`Error formatting date ${date}:`, error);
            // Return the expense with fallback formatting
            const fallbackDate = new Date();
            return {
              ...expense,
              date: format(fallbackDate, 'yyyy-MM-dd'),
              formattedDate: 'Invalid date',
              formattedAmount: `${CURRENCY_SYMBOLS_TEXT[expense.currency]}${parseFloat(expense.amount).toFixed(2)}`
            };
          }
        })
      )
      .sort((a, b) => {
        try {
          return new Date(b.date) - new Date(a.date); // Sort by date, newest first
        } catch (error) {
          return 0; // If dates can't be compared, keep original order
        }
      });
  }, [expenses]);

  const handleDeleteClick = (expense) => {
    setExpenseToDelete(expense);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (expenseToDelete) {
      await onDelete(expenseToDelete);
      setDeleteModalOpen(false);
      setExpenseToDelete(null);
    }
  };

  const handleEditClick = (expense) => {
    setEditingExpense(expense);
    setIsEditModalOpen(true);
  };

  const columns = React.useMemo(() => [
    {
      Header: 'Date',
      accessor: 'date',
      Cell: ({ value }) => {
        try {
          const parsedDate = parseISO(value);
          if (isNaN(parsedDate.getTime())) {
            return 'Invalid date';
          }
          return format(parsedDate, 'dd MMM yyyy');
        } catch (error) {
          return 'Invalid date';
        }
      },
      sortType: (rowA, rowB) => {
        try {
          const dateA = parseISO(rowA.original.date);
          const dateB = parseISO(rowB.original.date);
          
          // Check if either date is invalid
          if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
            return 0;
          }
          
          return dateB - dateA;
        } catch (error) {
          return 0;
        }
      }
    },
    {
      Header: 'Title',
      accessor: 'title',
      sortType: (rowA, rowB) =>
        rowA.values.title.toLowerCase().localeCompare(rowB.values.title.toLowerCase()),
      Cell: ({ row, value }) => (
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent sort trigger
            handleEditClick(row.original);
          }}
          className="text-indigo-600 hover:text-indigo-800 dark:text-orange-200 text-left"
        >
          <div className="truncate max-w-[300px] sm:max-w-[400px] md:max-w-[500px]" title={value}>
            {value}
          </div>
        </button>
      )
    },
    {
      Header: 'Category',
      accessor: 'category',
      sortType: 'alphanumeric'
    },
    {
      Header: 'Amount',
      accessor: 'formattedAmount',
      sortType: (rowA, rowB) => {
        const amountA = parseFloat(rowA.original.amount);
        const amountB = parseFloat(rowB.original.amount);
        return amountB - amountA;
      }
    },
    {
      Header: 'Actions',
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row }) => (
        <button
          onClick={() => handleDeleteClick(row.original)}
          className="text-red-500 hover:text-red-700"
        >
          <FaTrash />
        </button>
      )
    }
  ], []);

  return (
    <div className="p-6 dark:bg-gray-900 rounded-xl">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            Lifetime Expenses
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            View and manage all your expenses in one place
          </p>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-2">
            {getFirstExpenseDate() 
              ? `Lifetime Total (Since ${getFirstExpenseDate()}): `
              : "Lifetime Total: "
            }
            <span className="text-indigo-600 dark:text-indigo-400">{calculateLifetimeTotal()}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
            onClick={() => setIsBulkModalOpen(true)}
          >
            Add Bulk Expenses
          </button>
        </div>
      </div>
      
      <DataTableStructure
        columns={columns}
        data={tableData}
        initialPageSize={50}
        initialSortBy={[{ id: 'date', desc: false }]}
        globalFilter={tableFilter}
        setGlobalFilter={setTableFilter}
      />

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setExpenseToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={expenseToDelete?.title}
      />

      {/* Bulk Expense Modal */}
      {isBulkModalOpen && (
        <ExpenseModal
          onClose={() => setIsBulkModalOpen(false)}
          onSubmit={(data) => {
            onAddExpense(data, () => setIsBulkModalOpen(false));
          }}
          categories={categories}
          updateCategories={updateCategories}
          currencies={currencies}
          editingExpense={null}
          selectedDate={new Date()}
          bulkMode={true}
        />
      )}

      {/* Edit Expense Modal */}
      {isEditModalOpen && (
        <ExpenseModal
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingExpense(null);
          }}
          onSubmit={(updatedExpense) => {
            onEdit(updatedExpense);
            setIsEditModalOpen(false);
            setEditingExpense(null);
          }}
          categories={categories}
          updateCategories={updateCategories}
          currencies={currencies}
          editingExpense={editingExpense}
          selectedDate={editingExpense ? new Date(editingExpense.date) : new Date()}
        />
      )}
    </div>
  );
};

export default AllTimeExpenseView;
